import { publicUrl } from "../utils/publicUrl.js";
import { isTestEnvironment } from "../utils/runtimeEnv.js";
export const ART_MODE_QUERY_PARAM = "art";

let cachedWebpSupport = null;

function supportsWebp() {
  if (cachedWebpSupport != null) return cachedWebpSupport;
  if (typeof document === "undefined") {
    cachedWebpSupport = false;
    return cachedWebpSupport;
  }
  if (isTestEnvironment()) {
    cachedWebpSupport = false;
    return cachedWebpSupport;
  }

  try {
    const canvas = document.createElement("canvas");
    if (!canvas || typeof canvas.toDataURL !== "function") {
      cachedWebpSupport = false;
      return cachedWebpSupport;
    }
    cachedWebpSupport = canvas.toDataURL("image/webp").startsWith("data:image/webp");
    return cachedWebpSupport;
  } catch {
    cachedWebpSupport = false;
    return cachedWebpSupport;
  }
}

function portraitUrl(key) {
  const ext = supportsWebp() ? "webp" : "png";
  return publicUrl(`ui/portraits/${String(key)}.${ext}`);
}

export function getArtModeOverride() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(ART_MODE_QUERY_PARAM);
    if (raw === "1") return 1;
    if (raw === "0") return 0;
    return null;
  } catch {
    return null;
  }
}

export function isArtEnabled() {
  if (typeof window === "undefined") return false;
  try {
    const override = getArtModeOverride();
    if (override === 1) return true;
    if (override === 0) return false;
    return window.localStorage?.getItem("sm64_art") === "1";
  } catch {
    return false;
  }
}

export function setArtEnabled(isEnabled) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem("sm64_art", isEnabled ? "1" : "0");
  } catch {
    // ignore
  }
}

export function getCharacterArt(characterId) {
  const id = String(characterId || "").toLowerCase();

  // Initial integration: Rageburn / RAGEBURROW (Honey Badger)
  if (id === "honeybadger") {
    return {
      // Profile portraits (expressions)
      profileVariants: {
        neutral: portraitUrl("rageburrow_neutral"),
        angry: portraitUrl("rageburrow_angry"),
        damaged: portraitUrl("rageburrow_damaged"),
        shocked: portraitUrl("rageburrow_shocked"),
        smug: portraitUrl("rageburrow_smug"),
      },
      // Default portrait used by HUD + briefings
      profileUrl: portraitUrl("rageburrow_neutral"),
      // Trellis exports (GLB)
      fullProfileGlbUrl: publicUrl("glb/hero_profiles/rageburn_full_profile.glb"),
      cinematicProfileGlbUrl: publicUrl("glb/hero_profiles_lite/rageburn_full_profile.glb"),
      characterGlbUrl: publicUrl("glb/hero_profiles/rageburn_full_profile.glb"),
      shipGlbUrl: publicUrl("glb/hero_ships/rageburnship.glb"),
      shipGlbUrlActivated: publicUrl("glb/hero_ships_activated/rageburn_activated.glb"),
      // Player camera faces down -Z; most exports are modeled facing +Z.
      shipGlbRotation: { y: Math.PI },
    };
  }

  if (id === "octopus") {
    return {
      profileVariants: {
        neutral: portraitUrl("multiveil_neutral"),
        smug: portraitUrl("multiveil_amused"),
        shocked: portraitUrl("multiveil_calculating"),
        angry: portraitUrl("multiveil_threat"),
      },
      profileUrl: portraitUrl("multiveil_neutral"),
      fullProfileGlbUrl: publicUrl("glb/hero_profiles/multiveil_full_profile.glb"),
      cinematicProfileGlbUrl: publicUrl("glb/hero_profiles_lite/multiveil_full_profile.glb"),
      characterGlbUrl: publicUrl("glb/hero_profiles/multiveil_full_profile.glb"),
      shipGlbUrl: publicUrl("glb/hero_ships/multiveil_ship.glb"),
      shipGlbUrlActivated: publicUrl("glb/hero_ships_activated/multiveil_activated.glb"),
      // Player camera faces down -Z; exports are modeled facing +Z.
      shipGlbRotation: { y: Math.PI },
    };
  }

  if (id === "elephant") {
    return {
      profileVariants: {
        neutral: portraitUrl("graymatter_neutral"),
        shocked: portraitUrl("graymatter_concerned"),
        angry: portraitUrl("graymatter_stern"),
        smug: portraitUrl("graymatter_resolve"),
      },
      profileUrl: portraitUrl("graymatter_neutral"),
      fullProfileGlbUrl: publicUrl("glb/hero_profiles/gray_matter_full_profile.glb"),
      cinematicProfileGlbUrl: publicUrl("glb/hero_profiles_lite/gray_matter_full_profile.glb"),
      characterGlbUrl: publicUrl("glb/hero_profiles/gray_matter_full_profile.glb"),
      shipGlbUrl: publicUrl("glb/hero_ships/gray_matter_ship.glb"),
      shipGlbUrlActivated: publicUrl("glb/hero_ships_activated/gray_matter_activated.glb"),
      shipGlbRotation: { y: Math.PI },
    };
  }

  if (id === "mantisshrimp") {
    return {
      profileVariants: {
        neutral: portraitUrl("chromawar_composed"),
        smug: portraitUrl("chromawar_amused"),
        angry: portraitUrl("chromawar_wrath"),
      },
      profileUrl: portraitUrl("chromawar_composed"),
      fullProfileGlbUrl: publicUrl("glb/hero_profiles/chromawar_full_profile.glb"),
      cinematicProfileGlbUrl: publicUrl("glb/hero_profiles_lite/chromawar_full_profile.glb"),
      characterGlbUrl: publicUrl("glb/hero_profiles/chromawar_full_profile.glb"),
      shipGlbUrl: publicUrl("glb/hero_ships/chromawar_ship.glb"),
      shipGlbUrlActivated: publicUrl("glb/hero_ships_activated/chroma_activated.glb"),
      shipGlbRotation: { y: Math.PI },
    };
  }

  if (id === "ant") {
    return {
      profileVariants: {
        neutral: portraitUrl("fulfillmentqueen_neutral"),
        shocked: portraitUrl("fulfillmentqueen_alert"),
        angry: portraitUrl("fulfillmentqueen_alert"),
        smug: portraitUrl("fulfillmentqueen_victory"),
      },
      profileUrl: portraitUrl("fulfillmentqueen_neutral"),
      fullProfileGlbUrl: publicUrl("glb/hero_profiles/fufillmentqueen_full_profile.glb"),
      cinematicProfileGlbUrl: publicUrl("glb/hero_profiles_lite/fufillmentqueen_full_profile.glb"),
      characterGlbUrl: publicUrl("glb/hero_profiles/fufillmentqueen_full_profile.glb"),
      shipGlbUrl: publicUrl("glb/hero_ships/fufillmentqueen_ship.glb"),
      shipGlbUrlActivated: publicUrl("glb/hero_ships_activated/fufillment_activated.glb"),
      shipGlbRotation: { y: Math.PI },
    };
  }

  // Stage One supporting cast (2D portraits only)
  if (id === "admiralbulldog" || id === "admiral" || id === "admiralbloodhound") {
    const url = portraitUrl("admiral_bulldog");
    return {
      profileVariants: { neutral: url },
      profileUrl: url,
      fullProfileGlbUrl: null,
      characterGlbUrl: null,
      shipGlbUrl: null,
    };
  }

  if (id === "logi7" || id === "logi-7") {
    const url = portraitUrl("logi_7");
    return {
      profileVariants: { neutral: url },
      profileUrl: url,
      fullProfileGlbUrl: null,
      characterGlbUrl: null,
      shipGlbUrl: null,
    };
  }

  // System/Narration
  if (id === "narrator") {
    const url = portraitUrl("narrator");
    return {
      profileVariants: { neutral: url },
      profileUrl: url,
      fullProfileGlbUrl: null,
      characterGlbUrl: null,
      shipGlbUrl: null,
    };
  }

  // Boss portraits (2D) for comms / briefings / personality matrix.
  if (id === "marketmakeromega" || id === "marketmaker" || id === "omega") {
    const url = portraitUrl("market_maker_omega");
    return {
      profileVariants: { neutral: url },
      profileUrl: url,
      fullProfileGlbUrl: null,
      characterGlbUrl: null,
      shipGlbUrl: null,
    };
  }

  return null;
}
